<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title>Carousel Yönetimi</v-toolbar-title>
      <v-divider class="mx-3" inset vertical></v-divider>
      
      <v-select
        v-model="selectedTenantId"
        :items="tenantOptions"
        item-text="tenantName"
        item-value="tenantId"
        label="Tenant Seç"
        @change="onTenantChange"
        style="min-width: 150px; margin-right: 10px; margin-top: 20px;"
      ></v-select>
      
      <v-spacer></v-spacer>
      <v-btn color="primary" dark class="mb-2" @click="openDialog()">
        <v-icon left>add</v-icon>
        Yeni Ekle
      </v-btn>
    </v-toolbar>

    <v-container fluid v-if="selectedTenantId">
      <!-- Üst Carousel Listesi -->
      <div class="section-header">
        <v-icon left color="primary">arrow_upward</v-icon>
        <span class="section-title">Üst Carousel</span>
        <v-chip small color="primary" text-color="white" class="ml-2">{{ topCarousels.length }} adet</v-chip>
      </div>
      
      <v-row v-if="topCarousels.length === 0" class="mb-4">
        <v-col cols="12" class="text-center pa-4">
          <v-icon size="48" color="grey lighten-1">view_carousel</v-icon>
          <p class="mt-2 grey--text">Üst carousel için içerik eklenmemiş</p>
        </v-col>
      </v-row>

      <div class="carousel-list mb-6">
        <div
          v-for="(item, index) in topCarousels"
          :key="item.id"
          class="carousel-item"
          :class="{ 'inactive-item': !getIsActive(item) }"
          draggable="true"
          @dragstart="onDragStart($event, index, 'top')"
          @dragover.prevent
          @dragenter="onDragEnter($event, index, 'top')"
          @dragleave="onDragLeave($event)"
          @drop="onDrop($event, index, 'top')"
          @dragend="onDragEnd"
        >
          <div class="drag-handle">
            <span class="dot"></span><span class="dot"></span>
            <span class="dot"></span><span class="dot"></span>
            <span class="dot"></span><span class="dot"></span>
          </div>

          <div class="order-box">
            <input
              type="number"
              :value="getSortOrder(item)"
              min="1"
              class="order-input"
              @change="onOrderChange(item, $event.target.value, 'top')"
              @click.stop
            />
          </div>

          <div class="item-thumb" @click.stop>
            <div v-if="isVideo(item)" class="video-thumb-container" @click="openVideoModal(item)">
              <video
                :src="getImagePreview(item)"
                class="thumb-video"
                preload="metadata"
              ></video>
              <div class="play-overlay">
                <v-icon large color="white">play_circle_filled</v-icon>
              </div>
            </div>
            <v-img
              v-else
              :src="getImagePreview(item)"
              contain
              height="80"
              width="120"
            ></v-img>
          </div>

          <div class="item-info">
            <div class="item-title">{{ item.title || item.Title || 'Başlıksız' }}</div>
            <div class="item-meta">
              <span class="meta-chip" :class="getIsActive(item) ? 'active' : 'inactive'">
                {{ getIsActive(item) ? 'Aktif' : 'Pasif' }}
              </span>
              <span class="meta-chip type" :class="getTypeClass(item)">
                {{ getTypeLabel(item) }}
              </span>
              <span class="meta-chip media">
                <v-icon x-small>{{ isVideo(item) ? 'videocam' : 'image' }}</v-icon>
                {{ isVideo(item) ? 'Video' : 'Görsel' }}
              </span>
            </div>
          </div>

          <div class="item-actions">
            <button class="action-btn edit-btn" @click.stop="editItem(item)">
              <v-icon small>edit</v-icon>
            </button>
            <button class="action-btn delete-btn" @click.stop="deleteItem(item)">
              <v-icon small color="error">delete</v-icon>
            </button>
          </div>
        </div>
      </div>

      <v-divider class="my-4"></v-divider>

      <!-- Alt Carousel Listesi -->
      <div class="section-header">
        <v-icon left color="orange">arrow_downward</v-icon>
        <span class="section-title">Alt Carousel</span>
        <v-chip small color="orange" text-color="white" class="ml-2">{{ bottomCarousels.length }} adet</v-chip>
      </div>
      
      <v-row v-if="bottomCarousels.length === 0" class="mb-4">
        <v-col cols="12" class="text-center pa-4">
          <v-icon size="48" color="grey lighten-1">view_carousel</v-icon>
          <p class="mt-2 grey--text">Alt carousel için içerik eklenmemiş</p>
        </v-col>
      </v-row>

      <div class="carousel-list">
        <div
          v-for="(item, index) in bottomCarousels"
          :key="item.id"
          class="carousel-item"
          :class="{ 'inactive-item': !getIsActive(item) }"
          draggable="true"
          @dragstart="onDragStart($event, index, 'bottom')"
          @dragover.prevent
          @dragenter="onDragEnter($event, index, 'bottom')"
          @dragleave="onDragLeave($event)"
          @drop="onDrop($event, index, 'bottom')"
          @dragend="onDragEnd"
        >
          <div class="drag-handle">
            <span class="dot"></span><span class="dot"></span>
            <span class="dot"></span><span class="dot"></span>
            <span class="dot"></span><span class="dot"></span>
          </div>

          <div class="order-box">
            <input
              type="number"
              :value="getSortOrder(item)"
              min="1"
              class="order-input"
              @change="onOrderChange(item, $event.target.value, 'bottom')"
              @click.stop
            />
          </div>

          <div class="item-thumb" @click.stop>
            <div v-if="isVideo(item)" class="video-thumb-container" @click="openVideoModal(item)">
              <video
                :src="getImagePreview(item)"
                class="thumb-video"
                preload="metadata"
              ></video>
              <div class="play-overlay">
                <v-icon large color="white">play_circle_filled</v-icon>
              </div>
            </div>
            <v-img
              v-else
              :src="getImagePreview(item)"
              contain
              height="80"
              width="120"
            ></v-img>
          </div>

          <div class="item-info">
            <div class="item-title">{{ item.title || item.Title || 'Başlıksız' }}</div>
            <div class="item-meta">
              <span class="meta-chip" :class="getIsActive(item) ? 'active' : 'inactive'">
                {{ getIsActive(item) ? 'Aktif' : 'Pasif' }}
              </span>
              <span class="meta-chip type" :class="getTypeClass(item)">
                {{ getTypeLabel(item) }}
              </span>
              <span class="meta-chip media">
                <v-icon x-small>{{ isVideo(item) ? 'videocam' : 'image' }}</v-icon>
                {{ isVideo(item) ? 'Video' : 'Görsel' }}
              </span>
            </div>
          </div>

          <div class="item-actions">
            <button class="action-btn edit-btn" @click.stop="editItem(item)">
              <v-icon small>edit</v-icon>
            </button>
            <button class="action-btn delete-btn" @click.stop="deleteItem(item)">
              <v-icon small color="error">delete</v-icon>
            </button>
          </div>
        </div>
      </div>
    </v-container>

    <div v-else class="text-center pa-8">
      <v-icon size="64" color="grey lighten-1">store</v-icon>
      <h3 class="mt-4 grey--text">Lütfen bir Tenant seçiniz</h3>
    </div>

    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <carousel-image-upload v-model="editedItem.imageUrl"></carousel-image-upload>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.title"
                  label="Başlık *"
                  type="string"
                  outlined
                  dense
                  required
                  :rules="[v => !!v || 'Başlık zorunludur']"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.description"
                  label="Açıklama/Detay *"
                  type="string"
                  outlined
                  dense
                  required
                  :rules="[v => !!v || 'Açıklama zorunludur']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="editedItem.sortOrder"
                  label="Sıra *"
                  type="number"
                  outlined
                  dense
                  required
                  :rules="[v => v !== null && v !== undefined && v !== '' || 'Sıra zorunludur']"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="editedItem.isActive"
                  label="Aktif"
                ></v-checkbox>
              </v-col>

              <v-col cols="12" md="6">
                <div class="mb-2">
                  <span class="body-2 font-weight-medium">Sitede görüneceği alan</span>
                </div>
                <v-radio-group v-model="editedItem.position" required>
                  <v-radio 
                    label="Üst" 
                    :value="false"
                  ></v-radio>
                  <v-radio 
                    label="Alt" 
                    :value="true"
                  ></v-radio>
                </v-radio-group>
                <div v-if="editedItem.position === undefined || editedItem.position === null" class="caption error--text mt-1">
                  Konum seçimi zorunludur
                </div>
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.buttonName"
                  label="Buton Adı *"
                  outlined
                  dense
                  required
                  :rules="[v => !!v || 'Buton adı zorunludur']"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-radio-group v-model="editedItem.linkType" @change="onLinkTypeChange" required>
                  <v-radio label="Ürün" value="product"></v-radio>
                  <v-radio label="Kategori" value="category"></v-radio>
                  <v-radio label="Link" value="link"></v-radio>
                </v-radio-group>
                <div v-if="!editedItem.linkType" class="caption error--text mt-1">
                  Link tipi seçimi zorunludur
                </div>
              </v-col>

              <v-col cols="12" v-if="editedItem.linkType === 'product'">
                <v-autocomplete
                  v-model="selectedMpProduct"
                  :items="mpProducts"
                  :search-input.sync="mpProductSearch"
                  item-text="displayText"
                  outlined
                  dense
                  return-object
                  @change="onMpProductSelect"
                  @input="onMpProductInput"
                  :loading="mpProductsLoading"
                  no-data-text="Ürün bulunamadı"
                  placeholder="Ürün seçin veya arayın *"
                  required
                  :rules="[v => !!v || 'Ürün seçimi zorunludur']"
                >
                  <template v-slot:item="{ item }">
                    <div>
                      <div class="font-weight-bold">{{ item.product ? item.product.name : 'Bilinmeyen' }}</div>
                      <div class="caption grey--text">SKU: {{ item.product ? item.product.sku : item.mpSku }}</div>
                    </div>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span v-if="item">{{ item.product ? item.product.name : item.mpSku }}</span>
                    <span v-else>Ürün Seç</span>
                  </template>
                </v-autocomplete>
                <div v-if="editedItem.buttonLink" class="mt-2 caption grey--text">
                  Seçili SKU: <strong>{{ editedItem.buttonLink }}</strong>
                </div>
              </v-col>

              <v-col cols="12" v-if="editedItem.linkType === 'category'">
                <v-btn 
                  outlined 
                  color="primary" 
                  @click="openCategoryDialog"
                  block
                  :class="{ 'error--text': !selectedCategory }"
                >
                  <v-icon left>folder</v-icon>
                  {{ selectedCategory ? selectedCategory.name : 'Kategori Seç *' }}
                </v-btn>
                <div v-if="!selectedCategory" class="caption error--text mt-1">
                  Kategori seçimi zorunludur
                </div>
              </v-col>

              <v-col cols="12" v-if="editedItem.linkType === 'link'">
                <v-text-field
                  v-model="editedItem.buttonLink"
                  label="Link URL *"
                  placeholder="https://example.com"
                  outlined
                  dense
                  required
                  :rules="[v => !!v || 'Link URL zorunludur']"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="close">İptal</v-btn>
          <v-btn color="blue darken-1" text @click="save">Kaydet</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Video Player Modal -->
    <v-dialog v-model="videoModal" max-width="900px" @click:outside="closeVideoModal">
      <v-card class="video-modal-card">
        <v-card-title class="d-flex justify-space-between align-center pa-3">
          <span class="subtitle-1">{{ selectedVideoTitle }}</span>
          <v-btn icon @click="closeVideoModal">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <video
            v-if="selectedVideoUrl"
            ref="modalVideo"
            :src="selectedVideoUrl"
            class="modal-video"
            controls
            autoplay
          >
            Tarayıcınız video oynatmayı desteklemiyor.
          </video>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Category Selection Dialog -->
    <v-dialog v-model="categoryDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">Kategori Seç</span>
        </v-card-title>
        <v-card-text>
          <v-treeview
            v-if="categoryTree.length > 0"
            :items="categoryTree"
            item-key="id"
            item-text="name"
            item-children="children"
            hoverable
            open-on-click
            :active.sync="activeCategoryIds"
            activatable
            @update:active="onCategoryActive"
          >
            <template v-slot:prepend="{ item, open }">
              <v-icon
                v-if="item.children && item.children.length > 0"
                :class="{ 'rotate-icon': open }"
              >
                {{ open ? 'folder_open' : 'folder' }}
              </v-icon>
              <v-icon v-else color="grey lighten-1">
                label
              </v-icon>
            </template>
            <template v-slot:label="{ item }">
              <div class="d-flex align-center" @click.stop="selectCategory(item)">
                <span>{{ item.name }}</span>
                <v-chip small class="ml-2" color="blue lighten-4" text-color="blue darken-2">
                  {{ item.categoryKey }}
                </v-chip>
              </div>
            </template>
          </v-treeview>
          <div v-else class="text-center pa-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="categoryDialog = false">İptal</v-btn>
          <v-btn color="blue darken-1" text @click="confirmCategorySelection">Seç</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CarouselImageUpload from '@/components/CarouselImageUpload.vue';

export default {
  components: {
    CarouselImageUpload
  },
  data: () => ({
    selectedTenantId: null,
    tenantOptions: [
      { tenantId: 1, tenantName: "Altinda Firsat" },
      { tenantId: 2, tenantName: "EAltin" }
    ],
    dialog: false,
    editedIndex: -1,
    editedItem: {
      imageUrl: '',
      sortOrder: 0,
      isActive: true,
      title:'',
      description:'',
      buttonName: '',
      type: 1,
      buttonLink: '',
      linkType: null,
      isSecond: false,
      position: false
    },
    defaultItem: {
      imageUrl: '',
      sortOrder: 0,
      isActive: true,
      title:'',
      description:'',
      buttonName: '',
      type: 1,
      buttonLink: '',
      linkType: null,
      isSecond: false,
      position: false
    },
    mpProducts: [],
    mpProductSearch: '',
    selectedMpProduct: null,
    categoryTree: [],
    selectedCategory: null,
    categoryDialog: false,
    videoModal: false,
    selectedVideoUrl: null,
    selectedVideoTitle: '',
    draggedIndex: null,
    draggedSection: null,
    isDragging: false,
    placeholderImage: 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22200%22%20height%3D%22200%22%3E%3Crect%20fill%3D%22%23f0f0f0%22%20width%3D%22200%22%20height%3D%22200%22%2F%3E%3C%2Fsvg%3E',
    imagePreviewUrls: {},
    mpProductsLoading: false,
    activeCategoryIds: []
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'Yeni Carousel' : 'Carousel Düzenle';
    },
    carousels() {
      return this.$store.state.carousels.all;
    },
    topCarousels() {
      const items = this.carousels.filter(item => {
        const isSecond = item.isSecond !== undefined ? item.isSecond : (item.IsSecond !== undefined ? item.IsSecond : false);
        return !isSecond;
      });
      return items.sort((a, b) => {
        const aSortOrder = a.sortOrder !== undefined ? a.sortOrder : (a.SortOrder !== undefined ? a.SortOrder : 0);
        const bSortOrder = b.sortOrder !== undefined ? b.sortOrder : (b.SortOrder !== undefined ? b.SortOrder : 0);
        return aSortOrder - bSortOrder;
      });
    },
    bottomCarousels() {
      const items = this.carousels.filter(item => {
        const isSecond = item.isSecond !== undefined ? item.isSecond : (item.IsSecond !== undefined ? item.IsSecond : false);
        return isSecond;
      });
      return items.sort((a, b) => {
        const aSortOrder = a.sortOrder !== undefined ? a.sortOrder : (a.SortOrder !== undefined ? a.SortOrder : 0);
        const bSortOrder = b.sortOrder !== undefined ? b.sortOrder : (b.SortOrder !== undefined ? b.SortOrder : 0);
        return aSortOrder - bSortOrder;
      });
    },
    nextSortOrderTop() {
      if (this.topCarousels.length === 0) return 1;
      const maxOrder = Math.max(...this.topCarousels.map(c => c.sortOrder !== undefined ? c.sortOrder : (c.SortOrder !== undefined ? c.SortOrder : 0)));
      return maxOrder + 1;
    },
    nextSortOrderBottom() {
      if (this.bottomCarousels.length === 0) return 1;
      const maxOrder = Math.max(...this.bottomCarousels.map(c => c.sortOrder !== undefined ? c.sortOrder : (c.SortOrder !== undefined ? c.SortOrder : 0)));
      return maxOrder + 1;
    }
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    mpProductSearch() {
      this.searchMpProducts();
    }
  },

  mounted() {
    const savedTenantId = localStorage.getItem('selectedTenantId');
    if (savedTenantId && savedTenantId !== '0') {
      this.selectedTenantId = parseInt(savedTenantId);
      this.initialize();
    } else {
      this.$store.commit('carousels/setCarousels', []);
      this.imagePreviewUrls = {};
    }
    this.loadCategoryTree();
  },

  beforeDestroy() {
    // localStorage'ı temizleme - tenant seçimini koru
  },

  methods: {
    onTenantChange() {
      if (this.selectedTenantId) {
        localStorage.setItem('selectedTenantId', this.selectedTenantId.toString());
        // Tenant değiştiğinde önce listeyi temizle
        this.$store.commit('carousels/setCarousels', []);
        this.imagePreviewUrls = {};
        this.initialize();
      } else {
        // Tenant seçilmemişse listeyi temizle
        this.$store.commit('carousels/setCarousels', []);
        this.imagePreviewUrls = {};
      }
    },

    async initialize() {
      if (!this.selectedTenantId) {
        // Tenant yoksa listeyi temizle
        this.$store.commit('carousels/setCarousels', []);
        this.imagePreviewUrls = {};
        return;
      }
      
      try {
        // Yeni veri gelmeden önce listeyi temizle
        this.$store.commit('carousels/setCarousels', []);
        this.imagePreviewUrls = {};
        await this.$store.dispatch("carousels/getAllCarousels");
        await this.$nextTick();
        await this.loadImagePreviews();
      } catch (error) {
        // Hata durumunda da listeyi temizle
        this.$store.commit('carousels/setCarousels', []);
        this.imagePreviewUrls = {};
      }
    },

    async loadImagePreviews() {
      const Api = await import('@/api/baseApi').then(m => m.default);
      this.imagePreviewUrls = {};
      
      if (!this.carousels || this.carousels.length === 0) {
        return;
      }
      
      for (const carousel of this.carousels) {
        const carouselId = carousel.id || carousel.Id;
        
        if (carousel.imageUrl || carousel.ImageUrl) {
          const imageUrl = carousel.imageUrl || carousel.ImageUrl;
          try {
            if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('//')) {
              this.imagePreviewUrls[carouselId] = imageUrl;
            } else {
              const response = await Api.post('/api/upload/presigned-download-url', {
                fileKey: imageUrl
              });
              this.imagePreviewUrls[carouselId] = response.data.downloadUrl;
            }
          } catch (error) {
          }
        }
      }
      
      this.$forceUpdate();
    },

    openDialog() {
      if (!this.selectedTenantId) {
        alert("Lütfen önce bir Tenant seçiniz!");
        return;
      }
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedItem.position = false;
      this.editedItem.isSecond = false;
      this.editedItem.sortOrder = this.nextSortOrderTop;
      this.dialog = true;
    },

    async editItem(item) {
      this.editedIndex = this.carousels.indexOf(item);
      this.editedItem = Object.assign({}, item);
      
      const isSecond = item.isSecond !== undefined ? item.isSecond : (item.IsSecond !== undefined ? item.IsSecond : false);
      this.editedItem.position = isSecond;
      this.editedItem.isSecond = isSecond;
      
      this.selectedMpProduct = null;
      this.selectedCategory = null;
      this.mpProductSearch = '';
      this.activeCategoryIds = [];
      
      // Get the type value (1=Product, 2=Category, 3=Link)
      const itemType = item.type !== undefined ? item.type : (item.Type !== undefined ? item.Type : 1);
      this.editedItem.type = itemType;
      
      if (itemType === 2) {
        // Category type
        this.editedItem.linkType = 'category';
        await this.loadCategoryTree();
        if (item.buttonLink || item.ButtonLink) {
          const categoryKey = item.buttonLink || item.ButtonLink;
          const category = this.findCategoryByKey(this.categoryTree, categoryKey);
          if (category) {
            this.selectedCategory = category;
            this.activeCategoryIds = [category.id];
          }
        }
      } else if (itemType === 3) {
        // Link type
        this.editedItem.linkType = 'link';
        // buttonLink already contains the URL
      } else if (itemType === 1 && (item.buttonLink || item.ButtonLink)) {
        // Product type
        this.editedItem.linkType = 'product';
        const sku = item.buttonLink || item.ButtonLink;
        await this.loadMpProducts();
        const product = this.mpProducts.find(p => 
          (p.product && p.product.sku === sku) || p.mpSku === sku
        );
        if (product) {
          this.selectedMpProduct = product;
          this.mpProductSearch = product.product ? product.product.name : product.mpSku;
        } else {
          try {
            const Api = await import('@/api/mpProducts').then(m => m.default);
            const response = await Api.getMpProductsForCarousel();
            const allProducts = response.data || [];
            const foundProduct = allProducts.find(p => 
              (p.product && p.product.sku === sku) || p.mpSku === sku
            );
            if (foundProduct) {
              this.mpProducts.push({
                ...foundProduct,
                displayText: foundProduct.product ? `${foundProduct.product.name} (${foundProduct.product.sku})` : `${foundProduct.mpSku}`
              });
              this.selectedMpProduct = {
                ...foundProduct,
                displayText: foundProduct.product ? `${foundProduct.product.name} (${foundProduct.product.sku})` : `${foundProduct.mpSku}`
              };
              this.mpProductSearch = foundProduct.product ? foundProduct.product.name : foundProduct.mpSku;
            }
          } catch (error) {
          }
        }
      } else {
        this.editedItem.linkType = null;
      }
      
      this.dialog = true;
    },

    deleteItem(item) {
      if (confirm('Silmek istediğinizden emin misiniz?')) {
        const itemId = item.id || item.Id;
        this.$store.dispatch("carousels/deleteCarousel", itemId);
      }
    },

    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        this.selectedMpProduct = null;
        this.selectedCategory = null;
        this.mpProductSearch = '';
        this.activeCategoryIds = [];
      }, 300);
    },

    async save() {
      // Tüm zorunlu alanları kontrol et
      const imageUrl = this.editedItem.imageUrl || this.editedItem.ImageUrl;
      if (!imageUrl) {
        alert('Lütfen bir görsel veya video yükleyin!');
        return;
      }

      if (!this.editedItem.title || this.editedItem.title.trim() === '') {
        alert('Lütfen başlık giriniz!');
        return;
      }

      if (!this.editedItem.description || this.editedItem.description.trim() === '') {
        alert('Lütfen açıklama giriniz!');
        return;
      }

      if (this.editedItem.sortOrder === null || this.editedItem.sortOrder === undefined || this.editedItem.sortOrder === '') {
        alert('Lütfen sıra numarası giriniz!');
        return;
      }

      if (!this.editedItem.buttonName || this.editedItem.buttonName.trim() === '') {
        alert('Lütfen buton adı giriniz!');
        return;
      }

      if (this.editedItem.position === undefined || this.editedItem.position === null) {
        alert('Lütfen konum seçiniz (Üst veya Alt)!');
        return;
      }

      if (!this.editedItem.linkType) {
        alert('Lütfen link tipi seçiniz (Ürün veya Kategori)!');
        return;
      }

      if (this.editedItem.linkType === 'product' && (!this.selectedMpProduct || !this.editedItem.buttonLink)) {
        alert('Lütfen bir ürün seçiniz!');
        return;
      }

      if (this.editedItem.linkType === 'category' && (!this.selectedCategory || !this.editedItem.buttonLink)) {
        alert('Lütfen bir kategori seçiniz!');
        return;
      }

      if (this.editedItem.linkType === 'link' && (!this.editedItem.buttonLink || this.editedItem.buttonLink.trim() === '')) {
        alert('Lütfen bir link giriniz!');
        return;
      }

      this.editedItem.isSecond = this.editedItem.position === true;

      // Set the Type based on linkType (1=Product, 2=Category, 3=Link)
      if (this.editedItem.linkType === 'product') {
        this.editedItem.type = 1;
      } else if (this.editedItem.linkType === 'category') {
        this.editedItem.type = 2;
      } else if (this.editedItem.linkType === 'link') {
        this.editedItem.type = 3;
      }

      try {
        let savedCarousel;
        const isNewCarousel = this.editedIndex === -1;
        const targetSortOrder = this.editedItem.sortOrder;
        const isSecond = this.editedItem.isSecond;
        
        let oldIsSecond = null;
        let oldSortOrder = null;
        if (!isNewCarousel) {
          const originalItem = this.carousels[this.editedIndex];
          oldIsSecond = originalItem.isSecond !== undefined ? originalItem.isSecond : (originalItem.IsSecond !== undefined ? originalItem.IsSecond : false);
          oldSortOrder = originalItem.sortOrder !== undefined ? originalItem.sortOrder : (originalItem.SortOrder !== undefined ? originalItem.SortOrder : 0);
        }
        
        if (this.editedIndex > -1) {
          const itemId = this.editedItem.id || this.editedItem.Id;
          const response = await this.$store.dispatch("carousels/putCarousel", {
            id: itemId,
            carousel: this.editedItem
          });
          savedCarousel = response;
        } else {
          savedCarousel = await this.$store.dispatch("carousels/postCarousel", this.editedItem);
        }
        
        if (savedCarousel) {
          const sectionChanged = !isNewCarousel && oldIsSecond !== isSecond;
          const sortOrderChanged = !isNewCarousel && oldSortOrder !== targetSortOrder;
          
          if (isNewCarousel || sectionChanged || sortOrderChanged) {
            await this.reorderCarouselsAfterSave(savedCarousel, targetSortOrder, isSecond, sectionChanged, oldIsSecond);
          }
        }
        
        if (savedCarousel && savedCarousel.imageUrl) {
          await this.loadSingleImagePreview(savedCarousel);
        }
        
        this.close();
      } catch (error) {
        alert('Kaydetme sırasında hata oluştu!');
      }
    },

    async reorderCarouselsAfterSave(savedCarousel, targetSortOrder, isSecond, sectionChanged, oldIsSecond) {
      const savedCarouselId = savedCarousel.id || savedCarousel.Id;
      
      await this.reorderSection(isSecond, savedCarouselId, targetSortOrder);
      
      if (sectionChanged) {
        await this.reorderSection(oldIsSecond, null, null);
      }
    },

    async reorderSection(isSecond, excludeId, targetSortOrder) {
      const carouselList = isSecond ? this.bottomCarousels : this.topCarousels;
      
      const otherCarousels = carouselList.filter(c => {
        const cId = c.id || c.Id;
        return cId !== excludeId;
      });
      
      const sortedOthers = [...otherCarousels].sort((a, b) => {
        const aSortOrder = a.sortOrder !== undefined ? a.sortOrder : (a.SortOrder !== undefined ? a.SortOrder : 0);
        const bSortOrder = b.sortOrder !== undefined ? b.sortOrder : (b.SortOrder !== undefined ? b.SortOrder : 0);
        return aSortOrder - bSortOrder;
      });
      
      const updatedOrders = [];
      let currentOrder = 1;
      
      for (const carousel of sortedOthers) {
        if (targetSortOrder !== null && currentOrder === targetSortOrder) {
          currentOrder++;
        }
        
        const carouselId = carousel.id || carousel.Id;
        const currentCarouselOrder = carousel.sortOrder !== undefined ? carousel.sortOrder : (carousel.SortOrder !== undefined ? carousel.SortOrder : 0);
        
        if (currentCarouselOrder !== currentOrder) {
          updatedOrders.push({
            id: carouselId,
            sortOrder: currentOrder
          });
        }
        currentOrder++;
      }
      
      if (updatedOrders.length > 0) {
        try {
          await this.$store.dispatch("carousels/updateCarouselOrder", updatedOrders);
        } catch (error) {
          console.error('Reorder error:', error);
        }
      }
    },

    async loadSingleImagePreview(carousel) {
      const Api = await import('@/api/baseApi').then(m => m.default);
      const carouselId = carousel.id || carousel.Id;
      const imageUrl = carousel.imageUrl || carousel.ImageUrl;
      
      if (!imageUrl) return;
      
      try {
        if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('//')) {
          this.imagePreviewUrls[carouselId] = imageUrl;
        } else {
          const response = await Api.post('/api/upload/presigned-download-url', {
            fileKey: imageUrl
          });
          this.imagePreviewUrls[carouselId] = response.data.downloadUrl;
        }
        this.$forceUpdate();
      } catch (error) {
      }
    },

    getImagePreview(item) {
      const imageUrl = item.imageUrl || item.ImageUrl;
      const itemId = item.id || item.Id;
      
      if (!item || !imageUrl) return this.placeholderImage;
      
      if (this.imagePreviewUrls[itemId]) {
        return this.imagePreviewUrls[itemId];
      }
      
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('//')) {
        return imageUrl;
      }
      
      return this.placeholderImage;
    },

    getSortOrder(item) {
      return item.sortOrder !== undefined ? item.sortOrder : (item.SortOrder !== undefined ? item.SortOrder : 0);
    },

    getIsActive(item) {
      return item.isActive !== undefined ? item.isActive : (item.IsActive !== undefined ? item.IsActive : false);
    },

    getTypeClass(item) {
      const type = item.type !== undefined ? item.type : (item.Type !== undefined ? item.Type : 1);
      switch (type) {
        case 1: return 'product';
        case 2: return 'category';
        case 3: return 'link';
        default: return 'product';
      }
    },

    onDragStart(event, index, section) {
      this.draggedIndex = index;
      this.draggedSection = section;
      this.isDragging = true;
      event.dataTransfer.effectAllowed = 'move';
      event.target.style.opacity = '0.5';
    },

    onDragEnter(event, index, section) {
      if (this.draggedIndex !== null && this.draggedSection === section && this.draggedIndex !== index) {
        const item = event.target.closest('.carousel-item');
        if (item) {
          item.classList.add('drag-over');
        }
      }
    },

    onDragLeave(event) {
      const item = event.target.closest('.carousel-item');
      if (item) {
        item.classList.remove('drag-over');
      }
    },

    async onDrop(event, dropIndex, section) {
      event.preventDefault();
      const item = event.target.closest('.carousel-item');
      if (item) {
        item.classList.remove('drag-over');
      }
      
      // Farklı sectionlar arası sürüklemeye izin verme
      if (this.draggedSection !== section) {
        this.draggedIndex = null;
        this.draggedSection = null;
        this.isDragging = false;
        return;
      }
      
      if (this.draggedIndex !== null && this.draggedIndex !== dropIndex) {
        const items = section === 'top' ? [...this.topCarousels] : [...this.bottomCarousels];
        const draggedItem = items[this.draggedIndex];
        
        items.splice(this.draggedIndex, 1);
        items.splice(dropIndex, 0, draggedItem);
        
        // Yeni sıralama ile order array oluştur
        const newOrderData = items.map((item, idx) => ({
          id: item.id || item.Id,
          sortOrder: idx + 1
        }));
        
        try {
          await this.$store.dispatch("carousels/updateCarouselOrder", newOrderData);
        } catch (error) {
          console.error('Reorder error:', error);
          await this.$store.dispatch("carousels/getAllCarousels");
          alert('Sıralama güncellenirken hata oluştu!');
        }
      }
      
      this.draggedIndex = null;
      this.draggedSection = null;
      this.isDragging = false;
    },

    onDragEnd(event) {
      event.target.style.opacity = '1';
      this.isDragging = false;
      this.draggedSection = null;
      const items = document.querySelectorAll('.carousel-item');
      items.forEach(item => {
        item.classList.remove('drag-over');
      });
    },

    async onOrderChange(item, newOrder, section) {
      const order = parseInt(newOrder);
      if (isNaN(order) || order < 1) return;
      
      const currentOrder = this.getSortOrder(item);
      if (order === currentOrder) return;
      
      const items = section === 'top' ? [...this.topCarousels] : [...this.bottomCarousels];
      const currentIndex = items.findIndex(i => (i.id || i.Id) === (item.id || item.Id));
      const targetIndex = order - 1;
      
      if (targetIndex < 0 || targetIndex >= items.length) {
        alert('Geçersiz sıra numarası!');
        return;
      }
      
      const movedItem = items.splice(currentIndex, 1)[0];
      items.splice(targetIndex, 0, movedItem);
      
      const newOrderData = items.map((i, idx) => ({
        id: i.id || i.Id,
        sortOrder: idx + 1
      }));
      
      try {
        await this.$store.dispatch("carousels/updateCarouselOrder", newOrderData);
      } catch (error) {
        console.error('Order change error:', error);
        await this.$store.dispatch("carousels/getAllCarousels");
        alert('Sıralama güncellenirken hata oluştu!');
      }
    },

    onImageError(item) {
    },

    isVideo(item) {
      const imageUrl = item.imageUrl || item.ImageUrl;
      if (!imageUrl) return false;
      
      const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.wmv', '.flv', '.mkv'];
      const lowerUrl = imageUrl.toLowerCase();
      
      return videoExtensions.some(ext => lowerUrl.endsWith(ext));
    },

    getPositionLabel(item) {
      const isSecond = item.isSecond !== undefined ? item.isSecond : (item.IsSecond !== undefined ? item.IsSecond : false);
      return isSecond ? 'Alt' : 'Üst';
    },

    getTypeLabel(item) {
      const type = item.type !== undefined ? item.type : (item.Type !== undefined ? item.Type : 1);
      switch (type) {
        case 1:
          return 'Ürün';
        case 2:
          return 'Kategori';
        case 3:
          return 'Link';
        default:
          return 'Ürün';
      }
    },

    getTypeLabelColor(item) {
      const type = item.type !== undefined ? item.type : (item.Type !== undefined ? item.Type : 1);
      switch (type) {
        case 1:
          return 'purple';
        case 2:
          return 'orange';
        case 3:
          return 'teal';
        default:
          return 'purple';
      }
    },

    async loadCategoryTree() {
      try {
        const tree = await this.$store.dispatch('getCategoryTree');
        this.categoryTree = tree || [];
      } catch (error) {
        this.categoryTree = [];
      }
    },

    async searchMpProducts() {
      if (!this.mpProductSearch || this.mpProductSearch.length < 2) {
        this.mpProducts = [];
        return;
      }

      this.mpProductsLoading = true;
      try {
        const Api = await import('@/api/mpProducts').then(m => m.default);
        const response = await Api.getMpProductsForCarousel(this.mpProductSearch);
        const products = response.data || [];
        
        this.mpProducts = products.map(p => ({
          ...p,
          displayText: p.product ? `${p.product.name} (${p.product.sku})` : `${p.mpSku}`
        }));
      } catch (error) {
        this.mpProducts = [];
      } finally {
        this.mpProductsLoading = false;
      }
    },

    onMpProductSelect(product) {
      if (product && product.product) {
        this.editedItem.buttonLink = product.product.sku;
        this.editedItem.type = 1;
        this.mpProductSearch = product.product.name;
      } else if (product && product.mpSku) {
        this.editedItem.buttonLink = product.mpSku;
        this.editedItem.type = 1;
        this.mpProductSearch = product.mpSku;
      }
    },

    onMpProductInput(value) {
      if (!value) {
        this.selectedMpProduct = null;
        this.editedItem.buttonLink = '';
        this.mpProductSearch = '';
      }
    },

    onLinkTypeChange(newValue) {
      this.categoryDialog = false;
      this.selectedMpProduct = null;
      this.selectedCategory = null;
      this.editedItem.buttonLink = '';
      this.mpProductSearch = '';
      this.activeCategoryIds = [];
      this.mpProducts = [];
      
      if (newValue === 'product') {
        this.editedItem.type = 1;
        this.loadMpProducts();
      } else if (newValue === 'category') {
        this.editedItem.type = 2;
        this.loadCategoryTree();
      } else if (newValue === 'link') {
        this.editedItem.type = 3;
      }
    },

    async loadMpProducts() {
      try {
        const Api = await import('@/api/mpProducts').then(m => m.default);
        const response = await Api.getMpProductsForCarousel();
        const products = response.data || [];
        this.mpProducts = products.map(p => ({
          ...p,
          displayText: p.product ? `${p.product.name} (${p.product.sku})` : `${p.mpSku}`
        }));
      } catch (error) {
        this.mpProducts = [];
      }
    },

    findCategoryInTree(tree, id) {
      for (const item of tree) {
        if (item.id === id) {
          return item;
        }
        if (item.children && item.children.length > 0) {
          const found = this.findCategoryInTree(item.children, id);
          if (found) return found;
        }
      }
      return null;
    },

    onCategoryActive(activeIds) {
      this.activeCategoryIds = activeIds;
    },

    selectCategory(category) {
      this.activeCategoryIds = [category.id];
      this.selectedCategory = category;
    },

    confirmCategorySelection() {
      if (this.selectedCategory) {
        this.editedItem.buttonLink = this.selectedCategory.categoryKey;
        this.editedItem.type = 2;
        this.categoryDialog = false;
      } else if (this.activeCategoryIds && this.activeCategoryIds.length > 0) {
        const categoryId = this.activeCategoryIds[0];
        const category = this.findCategoryInTree(this.categoryTree, categoryId);
        
        if (category) {
          this.selectedCategory = category;
          this.editedItem.buttonLink = category.categoryKey;
          this.editedItem.type = 2;
          this.categoryDialog = false;
        }
      } else {
        alert('Lütfen bir kategori seçiniz!');
      }
    },

    findCategoryByKey(tree, categoryKey) {
      for (const item of tree) {
        if (item.categoryKey === categoryKey) {
          return item;
        }
        if (item.children && item.children.length > 0) {
          const found = this.findCategoryByKey(item.children, categoryKey);
          if (found) return found;
        }
      }
      return null;
    },

    async findMpProductBySku(sku) {
      try {
        await this.loadMpProducts();
        return this.mpProducts.find(p => 
          (p.product && p.product.sku === sku) || p.mpSku === sku
        );
      } catch (error) {
        return null;
      }
    },

    async openCategoryDialog() {
      await this.loadCategoryTree();
      this.categoryDialog = true;
    },

    openVideoModal(item) {
      this.selectedVideoUrl = this.getImagePreview(item);
      this.selectedVideoTitle = item.title || item.Title || 'Video Önizleme';
      this.videoModal = true;
    },

    closeVideoModal() {
      if (this.$refs.modalVideo) {
        this.$refs.modalVideo.pause();
      }
      this.videoModal = false;
      this.selectedVideoUrl = null;
      this.selectedVideoTitle = '';
    }
  }
};
</script>

<style scoped>
.section-header {
  display: flex;
  align-items: center;
  padding: 12px 0;
  margin-bottom: 8px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.carousel-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}

.carousel-item {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border-radius: 12px;
  padding: 12px 16px;
  cursor: grab;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.carousel-item:hover {
  background: #e8eef5;
}

.carousel-item:active {
  cursor: grabbing;
}

.carousel-item.drag-over {
  background: #d4e4f7;
  border: 2px dashed #5b8bd4;
  transform: scale(1.01);
}

.carousel-item.inactive-item {
  opacity: 0.6;
}

.drag-handle {
  display: grid;
  grid-template-columns: repeat(2, 6px);
  gap: 4px;
  padding: 8px;
  margin-right: 12px;
  cursor: grab;
  opacity: 0.4;
  transition: opacity 0.2s;
}

.carousel-item:hover .drag-handle {
  opacity: 0.7;
}

.drag-handle:active {
  cursor: grabbing;
}

.drag-handle .dot {
  width: 6px;
  height: 6px;
  background: #8fa4bf;
  border-radius: 50%;
}

.order-box {
  margin-right: 16px;
}

.order-input {
  width: 50px;
  height: 32px;
  border: 1px solid #d1d9e6;
  border-radius: 6px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  background: white;
  outline: none;
  transition: border-color 0.2s;
}

.order-input:focus {
  border-color: #5b8bd4;
}

.order-input::-webkit-inner-spin-button,
.order-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.item-thumb {
  width: 120px;
  height: 80px;
  min-width: 120px;
  background: #e8eef5;
  border-radius: 8px;
  margin-right: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-thumb-container {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.thumb-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  transition: background 0.2s ease;
}

.video-thumb-container:hover .play-overlay {
  background: rgba(0, 0, 0, 0.6);
}

.play-overlay .v-icon {
  font-size: 40px !important;
  opacity: 0.9;
  transition: transform 0.2s ease;
}

.video-thumb-container:hover .play-overlay .v-icon {
  transform: scale(1.1);
}

.video-modal-card {
  background: #000;
}

.video-modal-card .v-card__title {
  background: #1a1a1a;
  color: white;
}

.modal-video {
  width: 100%;
  max-height: 70vh;
  display: block;
}

.item-info {
  flex: 1;
  min-width: 0;
}

.item-title {
  font-weight: 600;
  font-size: 14px;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.meta-chip {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.meta-chip.active {
  background-color: #dcfce7;
  color: #166534;
}

.meta-chip.inactive {
  background-color: #fee2e2;
  color: #991b1b;
}

.meta-chip.type {
  background-color: #dbeafe;
  color: #1e40af;
}

.meta-chip.type.product {
  background-color: #f3e8ff;
  color: #7c3aed;
}

.meta-chip.type.category {
  background-color: #ffedd5;
  color: #c2410c;
}

.meta-chip.type.link {
  background-color: #ccfbf1;
  color: #0d9488;
}

.meta-chip.media {
  background-color: #f3f4f6;
  color: #4b5563;
}

.item-actions {
  display: flex;
  gap: 8px;
  margin-left: 16px;
}

.action-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.action-btn.edit-btn:hover {
  background: #dbeafe;
}

.action-btn.delete-btn:hover {
  background: #fee2e2;
}
</style>
